import { UnifiedTask } from '../components/TaskCard';
import { CalendarMonth, dayKeyOf, groupByDay, isInMonth } from './calendarMonth';

/** open: a work order with a dueDate; inspection: a kontroll on nextDueDate; completed: a work order on completedAt. */
export type CalendarEntryKind = 'open' | 'inspection' | 'completed';

export interface CalendarEntry {
    /** Unique across kinds (a work order key can only be open or completed, never both). */
    id: string;
    kind: CalendarEntryKind;
    /** `YYYY-MM-DD` the entry sits on. */
    day: string;
    overdue: boolean;
    task: UnifiedTask;
}

/** Only open work orders move by dragging; a kontroll's date is its schedule. */
export const isDraggable = (entry: CalendarEntry): boolean => entry.kind === 'open';

export function plannedEntry(task: UnifiedTask): CalendarEntry | null {
    const day = dayKeyOf(task.dueDate);
    if (!day) return null;
    const kind: CalendarEntryKind = task.type === 'workOrder' ? 'open' : 'inspection';
    return { id: `${kind}:${task.key}`, kind, day, overdue: task.isOverdue, task };
}

export function completedEntry(task: UnifiedTask): CalendarEntry | null {
    const day = dayKeyOf(task.workOrder?.completedAt);
    if (!day) return null;
    return { id: `completed:${task.key}`, kind: 'completed', day, overdue: false, task };
}

const KIND_ORDER: Record<CalendarEntryKind, number> = { open: 0, inspection: 1, completed: 2 };

const compareEntries = (a: CalendarEntry, b: CalendarEntry) =>
    Number(b.overdue) - Number(a.overdue)
    || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
    || a.task.title.localeCompare(b.task.title, 'sv');

/**
 * Place planned tasks (open work orders with a dueDate and kontroller) and completed
 * work orders on the days of `month`, each day sorted overdue → open → kontroll →
 * completed, then by title. Days of the neighbouring months shown in the grid stay
 * empty: completed orders are loaded per year, so they would be partial.
 */
export function buildMonthEntries(
    planned: UnifiedTask[],
    completed: UnifiedTask[],
    month: CalendarMonth
): Map<string, CalendarEntry[]> {
    const entries = [
        ...planned.map(plannedEntry),
        ...completed.map(completedEntry),
    ].filter((e): e is CalendarEntry => e !== null);

    const byDay = groupByDay(entries.filter(e => isInMonth(e.day, month)), e => e.day);
    byDay.forEach(list => list.sort(compareEntries));
    return byDay;
}
